import React, { useContext } from "react";

import { Preview, TextState } from "@creatomate/preview";
import { Button, Tooltip } from "@culturehq/components";

import { Subtitle, SubtitleSection } from "chq";
import { useProfile } from "app/context";
import { t, Trans } from "app/utils";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { createSection, updateSubtitleIndexesInCreatomate } from "../utils";

const SECTION_DURATION = 0.5;

interface AddSectionProps {
  preview: Preview;
  subtitleElement?: TextState;
}

const AddSection: React.FC<AddSectionProps> = ({ preview, subtitleElement }) => {
  const { data, handleAutosaveDraft } = useContext(CreatomateContext);
  const { subtitles } = data;

  const { profile: { organization } } = useProfile();

  const getSectionIndex = (): number => {
    const activeSub = subtitles.find(sub => sub.active);

    const activeSections = activeSub?.sections;

    return (activeSections?.findIndex(
      as => as.creatomateId === subtitleElement?.source.id)) ?? -1;
  };

  const updateSubtitles = (
    subtitles: Subtitle[], subtitleIndex: number, updatedSections: SubtitleSection[]
  ): Subtitle[] => (
    subtitles.map((subtitle, index) => (
      index === subtitleIndex ? { ...subtitle, sections: updatedSections } : subtitle
    ))
  );

  const adjustFollowingSections = (
    sections: SubtitleSection[],
    sectionIndex: number,
    newSectionEndTime: number,
    isActiveSubtitle: boolean
  ) => {
    let modifications = {};

    const adjustedSections = sections.map((section, index) => {
      let updatedSection = { ...section, index };

      if (index === sectionIndex + 2 && newSectionEndTime > updatedSection.startTime) {
        const updatedStartTime = newSectionEndTime + 0.001;
        const updatedEndTime = updatedStartTime
          + Math.max(updatedSection.endTime - updatedStartTime, 0.1);

        updatedSection = {
          ...updatedSection,
          startTime: updatedStartTime,
          originalStartTime: updatedStartTime,
          endTime: updatedEndTime,
          originalEndTime: updatedEndTime
        };

        if (isActiveSubtitle) {
          modifications = {
            [`${section.creatomateId}.time`]: updatedStartTime,
            [`${section.creatomateId}.duration`]: Math.max(updatedEndTime - updatedStartTime, 0)
          };
        }
      }

      return updatedSection;
    });

    return { updatedSections: adjustedSections, modifications };
  };

  const addNewSectionToSubtitle = (
    sections: SubtitleSection[] | undefined, sectionIndex: number, newSection: SubtitleSection
  ): SubtitleSection[] => {
    const updatedSections = sections ? [...sections] : [];
    updatedSections.splice(sectionIndex + 1, 0, newSection);
    return updatedSections;
  };

  const createNewSection = (
    id: string | undefined, index: number, startTime: number, endTime: number
  ): SubtitleSection => ({
    creatomateId: id,
    endTime,
    index,
    originalEndTime: endTime,
    originalStartTime: startTime,
    originalText: "New Section",
    startTime,
    text: "New Section",
    duration: endTime - startTime,
    track: subtitleElement?.track || 1
  });

  const handleAddSection = async () => {
    let updatedSubtitles: Subtitle[] = [...subtitles];
    const sectionIndex = getSectionIndex();
    const activeSubtitleIndex = subtitles.findIndex(subtitle => subtitle.active);
    const activeSubtitle = subtitles[activeSubtitleIndex];
    const currentSection = (activeSubtitle.sections || [])[sectionIndex];
    const newIndex = sectionIndex + 1;

    const newSectionStartTime = (currentSection?.endTime || 0) + 0.001;
    const newSectionEndTime = newSectionStartTime + SECTION_DURATION;

    const subtitleElementId = await createSection(
      preview,
      "New Section",
      newIndex,
      newSectionStartTime,
      newSectionEndTime,
      subtitleElement?.track || 1,
      organization.name || "",
      subtitleElement
    );

    const newSection = createNewSection(
      subtitleElementId, newIndex, newSectionStartTime, newSectionEndTime
    );

    for (let subtitleIndex = 0; subtitleIndex < subtitles.length; subtitleIndex += 1) {
      const updatedSections = addNewSectionToSubtitle(
        subtitles[subtitleIndex].sections, sectionIndex, newSection
      );
      const { updatedSections: adjustedSections, modifications } = adjustFollowingSections(
        updatedSections,
        sectionIndex,
        newSectionEndTime,
        subtitleIndex === activeSubtitleIndex
      );

      // eslint-disable-next-line no-await-in-loop
      await preview.applyModifications(modifications);

      updatedSubtitles = updateSubtitles(updatedSubtitles, subtitleIndex, adjustedSections);
    }

    handleAutosaveDraft({ subtitles: updatedSubtitles });
    await updateSubtitleIndexesInCreatomate(preview, updatedSubtitles);
  };

  if (!subtitleElement) {
    return (
      <Button className="Add-Section" icon="plus" inverted onClick={handleAddSection}>
        <Trans>Add section</Trans>
      </Button>
    );
  }

  return (
    <Tooltip tip={t("Add a new section below")}>
      <Button className="Add-Section" icon="plus" inverted onClick={handleAddSection} />
    </Tooltip>
  );
};

export default AddSection;
